import type { WorkspaceEntitlementsDto } from '@aiai/shared';

export type FuelLevel = 'ok' | 'low' | 'grounded';
export type Fuel =
  | { kind: 'unlimited' }
  | { kind: 'tank'; spentUsd: number; capUsd: number; ratio: number; level: FuelLevel };

/** From this share of the monthly cap the tank warns. */
export const LOW_FUEL = 0.8;

/**
 * A workspace's monthly budget as a fuel tank. The level is decided on the raw
 * spend/cap ratio: the spend cap really stops runs at 100%, so "grounded" only
 * shows at or above it. `ratio` is the unrounded value clamped to [0, 1].
 */
export function fuelOf(e: Pick<WorkspaceEntitlementsDto, 'monthlySpendCapUsd' | 'usage'>): Fuel {
  const cap = e.monthlySpendCapUsd;
  if (cap === null) return { kind: 'unlimited' };
  const rawSpent = e.usage.monthToDateSpendUsd;
  const spent = Number.isFinite(rawSpent) && rawSpent > 0 ? rawSpent : 0;
  const raw = cap <= 0 ? 1 : spent / cap;
  const ratio = Math.min(1, Math.max(0, raw));
  const level: FuelLevel = raw >= 1 ? 'grounded' : raw >= LOW_FUEL ? 'low' : 'ok';
  return { kind: 'tank', spentUsd: spent, capUsd: cap, ratio, level };
}

const usd = (n: number) => (Number.isInteger(n) ? `$${n}` : `$${n.toFixed(2)}`);

export function fuelText(workspaceName: string, f: Fuel): string {
  if (f.kind === 'unlimited') return `${workspaceName} ∞`;
  const flag = f.level === 'grounded' ? ' · grounded' : f.level === 'low' ? ' · low fuel' : '';
  // floor, so the percent never reads 100% before the tank is actually grounded
  const pct = Math.floor(f.ratio * 100 + 1e-9);
  return `${workspaceName} $${f.spentUsd.toFixed(2)} of ${usd(f.capUsd)} (${pct}%)${flag}`;
}
